export interface ServiceData {
  slug: string;
  title: string;
  subtitle: string;
  price: string;
  /** Exactly one image per service — reused on the card and the detail hero. */
  image: string;
  imageAlt: string;
  description: string;
  features: string[];
  process: { step: string; desc: string }[];
  /** Route-level SEO. Title stays under 60 chars, description 145–155 chars. */
  metaTitle: string;
  metaDescription: string;
}

export const services: ServiceData[] = [
  {
    slug: "drywall-repair",
    title: "Drywall Repair",
    subtitle: "Patches, cracks, holes, and seamless texture matching",
    price: "Free Quote",
    image: "/assets/images/service-drywall-repair.webp",
    imageAlt: "Handyman patching and sanding a repaired drywall section in a Hayward home",
    description:
      "Doorknob holes, settling cracks, water stains, popped nails, failed tape seams — we repair drywall so the fix disappears into the wall instead of announcing itself. Every patch is cut square, backed properly, taped, floated in multiple coats, and sanded flat before we texture-match the surrounding surface. The result is a wall you can paint the same day without a shadow line giving the repair away.",
    features: [
      "Small patch repair for doorknob, anchor, and nail holes",
      "Large section replacement for water or impact damage",
      "Crack repair with mesh or paper tape reinforcement",
      "Orange peel, knockdown, and smooth texture matching",
      "Ceiling drywall repair, including popcorn removal",
      "Dust-controlled sanding and full cleanup",
    ],
    process: [
      { step: "Inspect the Damage", desc: "We check what's behind the wall first — a leak or loose framing has to be handled before any patch goes on." },
      { step: "Cut & Back the Patch", desc: "Damage is cut back to a clean square and backed with furring or blocking so the patch never flexes." },
      { step: "Tape, Float & Sand", desc: "Multiple thin coats of compound, feathered wide, then sanded flat so no ridge shows under paint." },
      { step: "Match the Texture", desc: "We spray or hand-apply texture to match the existing wall, then clean the space completely." },
    ],
    metaTitle: "Drywall Repair in Hayward, CA | Handyman Luxestuardo",
    metaDescription:
      "Expert drywall repair in Hayward and the East Bay. Holes, cracks, water damage, and seamless texture matching. Free estimates — call (510) 600-9216.",
  },
  {
    slug: "interior-exterior-painting",
    title: "Interior & Exterior Painting",
    subtitle: "Clean lines, full prep, and finishes that hold up",
    price: "Free Quote",
    image: "/assets/images/service-interior-exterior-painting.webp",
    imageAlt: "Painter cutting in a crisp line on an interior wall during a repaint",
    description:
      "A paint job is mostly prep, and prep is where most jobs get shortchanged. We wash, scrape, sand, caulk, patch, and prime before a single finish coat goes on — because a premium topcoat over a poor surface still fails. Interior or exterior, we protect what isn't being painted, cut crisp lines by hand, and roll consistent coverage so the finish looks even in every light.",
    features: [
      "Full surface prep: wash, scrape, sand, patch, and caulk",
      "Spot or full priming, including stain-blocking primer",
      "Interior walls, ceilings, trim, doors, and cabinets",
      "Exterior siding, stucco, fascia, soffits, and trim",
      "Floors, fixtures, and landscaping fully masked and protected",
      "Color consultation and sample application on request",
    ],
    process: [
      { step: "Walk & Estimate", desc: "We walk the space with you, note repairs, confirm colors and sheens, and put the scope in writing." },
      { step: "Protect & Prep", desc: "Everything that isn't getting painted is covered. Surfaces are cleaned, sanded, patched, and primed." },
      { step: "Cut & Coat", desc: "Hand-cut edges and even roller or spray coverage — the number of coats the color actually needs." },
      { step: "Detail & Walk Through", desc: "We pull tape, touch up under daylight, clean up, and review the finish with you before we leave." },
    ],
    metaTitle: "Interior & Exterior Painting Hayward | Handyman Luxestuardo",
    metaDescription:
      "Interior and exterior house painting in Hayward, CA. Full prep, crisp lines, durable finishes for walls, trim, and siding. Free estimate: (510) 600-9216.",
  },
  {
    slug: "plumbing",
    title: "Plumbing",
    subtitle: "Leaks, fixtures, valves, and water heater work",
    price: "Free Quote",
    image: "/assets/images/service-plumbing.webp",
    imageAlt: "Plumbing repair under a kitchen sink with supply lines and shutoff valves",
    description:
      "Most household plumbing problems are small until they aren't. We handle the everyday work that keeps a home dry and running: dripping faucets, running toilets, slow drains, corroded shutoff valves, leaking supply lines, and water heater replacement. Repairs are done with proper materials and correct connections — not tape and hope — and we test everything under pressure before we call it finished.",
    features: [
      "Faucet, sink, and shower valve repair or replacement",
      "Toilet repair, reseating, and full replacement",
      "Water heater installation and replacement",
      "Shutoff valve and supply line replacement",
      "Drain clearing and slow-drain diagnosis",
      "Under-sink leak repair and cabinet water damage fixes",
    ],
    process: [
      { step: "Diagnose the Source", desc: "We trace the actual source of the leak or blockage instead of treating the symptom you can see." },
      { step: "Confirm the Fix", desc: "You get a clear explanation of what needs replacing, what it costs, and what happens if it waits." },
      { step: "Repair & Replace", desc: "Correct fittings, new supply lines and valves where needed, and connections made to hold." },
      { step: "Pressure Test & Clean", desc: "Everything is run and checked for drips, then the workspace is dried and cleaned." },
    ],
    metaTitle: "Plumbing Repairs in Hayward, CA | Handyman Luxestuardo",
    metaDescription:
      "Plumbing repairs in Hayward and the East Bay: faucets, toilets, valves, leaks, and water heater installation. Free estimates — call (510) 600-9216 today.",
  },
  {
    slug: "electrical-services",
    title: "Electrical Services",
    subtitle: "Fixtures, switches, outlets, and lighting upgrades",
    price: "Free Quote",
    image: "/assets/images/service-electrical.webp",
    imageAlt: "Electrical outlet and switch wiring being installed in a residential wall box",
    description:
      "Older Bay Area homes are full of tired electrical: cloth-wrapped fixture leads, two-prong outlets, switches that buzz, and junction boxes stuffed past capacity. We handle residential fixture and device work — replacing light fixtures, switches, dimmers, outlets, and adding lighting where a room needs it — with the box, wire gauge, and connections checked rather than assumed.",
    features: [
      "Light fixture replacement, indoor and outdoor",
      "Recessed and under-cabinet lighting installation",
      "Switch, dimmer, and three-way switch replacement",
      "Outlet replacement, including GFCI in wet locations",
      "Smart switch and smart dimmer installation",
      "Loose box, damaged device, and flickering circuit diagnosis",
    ],
    process: [
      { step: "Confirm What's There", desc: "Power off, cover off, and a look at the wiring and box before we quote the work." },
      { step: "Plan the Upgrade", desc: "We tell you what can be swapped directly and what needs a box, wire, or circuit change first." },
      { step: "Install Cleanly", desc: "Secure connections, correct grounding, and devices set flush and level in the wall." },
      { step: "Test Every Circuit", desc: "We power up, test each device and switch leg, and confirm everything reads correctly." },
    ],
    metaTitle: "Electrical Services in Hayward, CA | Handyman Luxestuardo",
    metaDescription:
      "Residential electrical work in Hayward, CA: light fixtures, switches, dimmers, outlets, and recessed lighting. Free estimates — call (510) 600-9216.",
  },
  {
    slug: "door-installation",
    title: "Door Installation",
    subtitle: "Interior, exterior, and closet doors hung true",
    price: "Free Quote",
    image: "/assets/images/service-door-installation.webp",
    imageAlt: "New interior door being fitted and shimmed into a level frame",
    description:
      "A door that sticks, swings open on its own, or shows a wedge of daylight along one edge is almost always a framing and shimming problem, not a door problem. We hang interior, exterior, and closet doors plumb and square, set consistent reveals, adjust the strike so the latch catches without a shove, and case the opening cleanly so the finished trim sits tight to the wall.",
    features: [
      "Interior pre-hung and slab door installation",
      "Exterior and entry door replacement with weatherstripping",
      "Closet, bifold, barn, and pocket door installation",
      "Hardware, lockset, and deadbolt installation",
      "Casing, jamb extension, and trim finishing",
      "Sticking, sagging, and misaligned door correction",
    ],
    process: [
      { step: "Measure the Opening", desc: "Rough opening, jamb depth, swing direction, and floor level all get checked before ordering." },
      { step: "Set Plumb & Square", desc: "The frame is shimmed and fastened so the door hangs true and stays where you leave it." },
      { step: "Fit Hardware", desc: "Hinges, latch, strike, and lockset installed and adjusted for a clean, quiet close." },
      { step: "Case & Finish", desc: "Casing installed tight, gaps caulked, and nail holes filled ready for paint or stain." },
    ],
    metaTitle: "Door Installation in Hayward, CA | Handyman Luxestuardo",
    metaDescription:
      "Interior, exterior, and closet door installation in Hayward, CA. Doors hung plumb with clean casing and correct hardware. Free quote: (510) 600-9216.",
  },
  {
    slug: "ceiling-fan-installation",
    title: "Fan Installation",
    subtitle: "Ceiling fans, bath fans, and vented exhaust",
    price: "Free Quote",
    image: "/assets/images/service-ceiling-fan-installation.webp",
    imageAlt: "New industrial ventilation fans ready for installation",
    description:
      "A ceiling fan hung from a standard light box will eventually wobble itself loose — fans need a fan-rated box anchored to structure. We install ceiling fans, bathroom exhaust fans, and range or whole-house ventilation with the right box, the right bracing, and the right duct run, so the fan is quiet, balanced, and actually moving air where it's supposed to go.",
    features: [
      "Ceiling fan installation with fan-rated box and bracing",
      "Fan replacement and upgrade on existing wiring",
      "Bathroom exhaust fan installation and venting",
      "Fan and light combination units with dual switching",
      "Remote and wall-control setup",
      "Wobble, noise, and balance correction on existing fans",
    ],
    process: [
      { step: "Check the Box", desc: "We confirm whether the existing box is fan-rated and anchored — most aren't, and we replace them." },
      { step: "Brace & Mount", desc: "A rated box is secured to joists or an expanding brace so the mount carries the load properly." },
      { step: "Wire & Assemble", desc: "Correct switch legs for fan and light, then blades balanced and canopy set flush to the ceiling." },
      { step: "Run & Verify", desc: "We run every speed and direction, check for wobble or hum, and confirm venting is clear." },
    ],
    metaTitle: "Fan Installation in Hayward, CA | Handyman Luxestuardo",
    metaDescription:
      "Ceiling fan and bathroom exhaust fan installation in Hayward, CA. Fan-rated boxes, proper bracing, quiet balanced results. Free quote: (510) 600-9216.",
  },
  {
    slug: "garbage-disposal-replacement",
    title: "Garbage Disposal Replacement",
    subtitle: "Swap-outs, jams, leaks, and sink drain repair",
    price: "Free Quote",
    image: "/assets/images/service-garbage-disposal-replacement.webp",
    imageAlt: "Handyman installing a new garbage disposal unit under a stainless steel kitchen sink",
    description:
      "When a disposal hums instead of grinding, leaks at the flange, or trips its reset every time you use it, replacement is usually cheaper than repeated repairs. We remove the old unit, replace the sink flange and gasket rather than reusing tired ones, reconnect the drain and dishwasher line correctly, and test under running water so you know it's dry before we pack up.",
    features: [
      "Full garbage disposal removal and replacement",
      "New sink flange, gasket, and mounting assembly",
      "Dishwasher drain line connection and knockout",
      "Jam clearing and reset troubleshooting",
      "Leak repair at flange, gasket, and drain connections",
      "Under-sink drain and P-trap reconfiguration",
    ],
    process: [
      { step: "Assess the Unit", desc: "We check whether it's a jam, a motor failure, or a leak — and tell you honestly which it is." },
      { step: "Remove & Reseal", desc: "Old unit out, flange and gasket replaced so the new install doesn't inherit an old leak." },
      { step: "Mount & Connect", desc: "New disposal mounted, drain and dishwasher line connected with proper slope and fittings." },
      { step: "Run & Check Dry", desc: "We run water and the disposal together, then check every joint by hand for moisture." },
    ],
    metaTitle: "Garbage Disposal Replacement Hayward | Handyman Luxestuardo",
    metaDescription:
      "Garbage disposal replacement and repair in Hayward, CA. Leaks, jams, and full swap-outs with new flange and gasket. Free estimate: (510) 600-9216.",
  },
  {
    slug: "molding-trim-installation",
    title: "Molding Installation",
    subtitle: "Baseboards, crown, casing, and finish carpentry",
    price: "Free Quote",
    image: "/assets/images/service-molding-trim-installation.webp",
    imageAlt: "Carpenter nailing custom molding and trim into place",
    description:
      "Trim is the detail people notice without knowing why. Walls aren't straight and corners aren't ninety degrees, so good trim work is about coping inside corners, tuning miters to the actual angle, and scribing to what's really there. We install baseboard, crown, door and window casing, chair rail, and wainscoting — then fill, caulk, and sand so the run reads as one continuous line.",
    features: [
      "Baseboard installation and replacement",
      "Crown molding with coped inside corners",
      "Door and window casing, including jamb extensions",
      "Chair rail, picture rail, and wainscoting",
      "Shoe molding and quarter round at flooring transitions",
      "Nail holes filled, seams caulked, sanded ready for paint",
    ],
    process: [
      { step: "Measure & Plan Runs", desc: "We lay out the run to minimize seams and place any necessary joints where they read least." },
      { step: "Cut to the Real Angle", desc: "Corners get measured and coped or mitered to the angle that's actually there, not the one on the plan." },
      { step: "Fasten & Draw Tight", desc: "Trim is nailed to framing, joints glued and drawn tight so they don't open with seasonal movement." },
      { step: "Fill, Caulk & Sand", desc: "Holes filled, top edge and corners caulked, then sanded smooth and ready for finish." },
    ],
    metaTitle: "Molding & Trim Carpentry Hayward | Handyman Luxestuardo",
    metaDescription:
      "Baseboard, crown molding, casing, and finish carpentry in Hayward, CA. Coped corners and paint-ready seams. Free estimate — call (510) 600-9216 today.",
  },
  {
    slug: "deck-installation",
    title: "Deck Installation",
    subtitle: "New decks, boards, railings, and stair repair",
    price: "Free Quote",
    image: "/assets/images/service-deck-installation.webp",
    imageAlt: "Newly built wood deck with fresh boards and railing in a backyard",
    description:
      "A deck lives outdoors in Bay Area moisture and sun, so how it's built matters more than how it looks on day one. We build and repair decks with attention to the parts that fail first: ledger attachment and flashing, post bases held off concrete, joist spacing that doesn't let boards cup, and fasteners that won't bleed rust down the face of the wood.",
    features: [
      "New deck construction, framing through decking",
      "Deck board replacement and full resurfacing",
      "Railing, post, and baluster installation",
      "Stair and stringer construction and repair",
      "Ledger flashing and rot repair at the house connection",
      "Sanding, staining, and sealing for weather protection",
    ],
    process: [
      { step: "Assess & Design", desc: "We look at the space, the existing structure if there is one, and agree on layout, material, and budget." },
      { step: "Frame It Right", desc: "Proper footings, post bases, ledger flashing, and joist spacing — the parts nobody sees but everybody feels." },
      { step: "Lay the Decking", desc: "Boards set with consistent gapping and fastened so nothing cups, squeaks, or rusts through." },
      { step: "Finish & Seal", desc: "Edges sanded, railings set solid, and the surface stained or sealed against sun and rain." },
    ],
    metaTitle: "Deck Installation in Hayward, CA | Handyman Luxestuardo",
    metaDescription:
      "Deck building and repair in Hayward, CA: new decks, board replacement, railings, and stairs built for Bay Area weather. Free quote: (510) 600-9216.",
  },
  {
    slug: "shower-door-installation",
    title: "Shower Door Installation",
    subtitle: "Frameless, framed, sliding, and pivot doors",
    price: "Free Quote",
    image: "/assets/images/service-shower-door-installation.webp",
    imageAlt: "Glass shower enclosure installed in a modern tiled walk-in shower",
    description:
      "Shower doors are unforgiving — walls out of plumb, an out-of-level curb, or a badly placed sealant bead turns into a door that drags or a puddle on the bathroom floor. We install frameless, semi-frameless, framed, sliding, and pivot enclosures, shimming to the reality of the opening and sealing to the wet side only so water drains back into the pan instead of behind the tile.",
    features: [
      "Frameless and semi-frameless glass enclosure installation",
      "Sliding, bypass, and pivot door installation",
      "Framed shower and tub door replacement",
      "Old door and track removal with clean substrate prep",
      "Correct wet-side sealing and drainage detailing",
      "Hardware, handle, sweep, and seal replacement",
    ],
    process: [
      { step: "Measure the Reality", desc: "Plumb, level, and square are all measured — enclosures are ordered to the opening, not to a catalogue." },
      { step: "Prep the Opening", desc: "Old track and sealant removed, substrate cleaned, and anchor points confirmed in solid backing." },
      { step: "Set & Shim Level", desc: "Glass and hardware set plumb and level so the door swings or slides freely and hangs square." },
      { step: "Seal & Water Test", desc: "Sealant applied on the wet side only, cured, then the enclosure is run and checked for leaks." },
    ],
    metaTitle: "Shower Door Installation Hayward | Handyman Luxestuardo",
    metaDescription:
      "Shower door installation in Hayward, CA. Frameless, sliding, and framed enclosures set level and sealed to drain correctly. Free quote: (510) 600-9216.",
  },
];

export const getService = (slug?: string) => services.find((s) => s.slug === slug);
